import {useMemo, useRef} from "react";
import {Trans, useTranslation} from "react-i18next";
import {useHistory, useRouteMatch} from "react-router-dom";

import {IMonthCityStats} from "../../../../../api/src/db_queries/month_city_stats_query";
import {IMonthCityTypeStats} from "../../../../../api/src/db_queries/month_city_type_stats_query";
import {formatDate, humanDateFormatLong, humanDateFormatShort} from "../../../../../utils/dates";
import {useAppDispatch, useAppSelector} from "../../../../../utils/hooks/store_hooks";
import {useAccessDate} from "../../../../../utils/hooks/useAccessDate";
import {FilterOfferType} from "../../../common/app/constants/enums";
import {chartsRoutes} from "../../../common/app/routing/charts_routes";
import {mapPathnameToOfferType, OfferType} from "../../../common/app/routing/offer_type";
import {Card} from "../../../components/card/Card";
import {MultilineSharedChart} from "../../../components/charts/MultilineShared";
import {Error} from "../../../components/Error";
import {Grid} from "../../../components/Grid";
import {withDateGuard} from "../../../components/hoc/withDateGuard";
import {withGTM} from "../../../components/hoc/withGTM";
import {ChartsLayout as Layout} from "../../../components/layout/charts_layout/ChartsLayout";
import {Division, MathEquation} from "../../../components/MathEquation";
import {selectLatestEntry} from "../../ssr/redux/latest_entry_slice";
import {pageviewAlgolyticsHit} from "../../tracking/algolytics/pageview_hits";
import {usePageview} from "../../tracking/use_pageview";
import {ViewType} from "../../tracking/view_type";
import {
    useGetChartsBiggestRegionsStatsQuery,
    useGetChartsBiggestRegionsTypeStatsQuery
} from "../api/charts_platform_api";
import {AnalystComment} from "../components/AnalystComment";
import {CountryMap} from "../components/country_map/CountryMap";
import {ExtendableTextBlock} from "../components/ExtendableTextBlock";
import {mapRtkQueryStatusToRequestStatus} from "../utils/rtq_status_to_request_status";

interface IHomeConfig {
    filterOfferType: FilterOfferType | null;
    pathOfferType: OfferType;
    cityRouteRoot: string;
    propertyTypeChart1: string;
    propertyTypeRest: string;
    useTypeStats: boolean;
}

const getHomeConfig = (offerType: OfferType, language: string): IHomeConfig => {
    const routes = chartsRoutes[language as keyof typeof chartsRoutes];

    switch (offerType) {
        case OfferType.FLAT:
            return {
                filterOfferType: FilterOfferType.FLATS,
                pathOfferType: OfferType.FLAT,
                cityRouteRoot: routes.flats.city.root,
                propertyTypeChart1: "flat",
                propertyTypeRest: "flats",
                useTypeStats: true
            };
        case OfferType.HOUSE:
            return {
                filterOfferType: FilterOfferType.HOUSES,
                pathOfferType: OfferType.HOUSE,
                cityRouteRoot: routes.houses.city.root,
                propertyTypeChart1: "house",
                propertyTypeRest: "houses",
                useTypeStats: true
            };
        default:
            return {
                filterOfferType: null,
                pathOfferType: OfferType.PROPERTY,
                cityRouteRoot: routes.properties.city.root,
                propertyTypeChart1: "properties",
                propertyTypeRest: "properties",
                useTypeStats: false
            };
    }
};

export const Home = withDateGuard(
    withGTM(() => {
        const {t, i18n} = useTranslation();
        const {path} = useRouteMatch();
        const offerType = mapPathnameToOfferType(path);
        const homeConfig = getHomeConfig(offerType, i18n.language);
        const {currentDate} = useAppSelector(selectLatestEntry);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore withDateGuard makes sure currentDate is not null
        const {upperBound, lowerBound} = useAccessDate(currentDate);
        const history = useHistory();
        const dispatch = useAppDispatch();
        usePageview(() => {
            pageviewAlgolyticsHit({offerType, viewType: ViewType.HOME});
        });

        const homeQueryParams = {
            date_start: lowerBound,
            date_end: upperBound,
            scenario: "home"
        };

        const {
            data: biggestRegionsStatsData,
            isSuccess: rtqBiggestRegionsStatsIsSuccess,
            isError: rtqBiggestRegionsStatsIsError
        } = useGetChartsBiggestRegionsStatsQuery(homeQueryParams);

        const {
            data: biggestRegionsTypeStatsData,
            isSuccess: rtqBiggestRegionsTypeStatsIsSuccess,
            isError: rtqBiggestRegionsTypeStatsIsError
        } = useGetChartsBiggestRegionsTypeStatsQuery(homeQueryParams);

        const biggestRegionsStatsDataStatus = mapRtkQueryStatusToRequestStatus(
            rtqBiggestRegionsStatsIsError,
            rtqBiggestRegionsStatsIsSuccess
        );

        const biggestRegionsTypeStatsDataStatus = mapRtkQueryStatusToRequestStatus(
            rtqBiggestRegionsTypeStatsIsError,
            rtqBiggestRegionsTypeStatsIsSuccess
        );

        const chartData = homeConfig.useTypeStats ? biggestRegionsTypeStatsData : biggestRegionsStatsData;
        const chartDataStatus = homeConfig.useTypeStats
            ? biggestRegionsTypeStatsDataStatus
            : biggestRegionsStatsDataStatus;

        const mapData = useMemo((): (IMonthCityStats | IMonthCityTypeStats)[] => {
            if (chartData && chartData.length > 0) {
                return chartData.filter((month) => month.date === upperBound);
            }
            return [];
        }, [chartData, upperBound]);

        const chart1Ref = useRef(null);
        const chart2Ref = useRef(null);
        const chart3Ref = useRef(null);
        const chart4Ref = useRef(null);
        const chart5Ref = useRef(null);

        const periodDates = `${formatDate(lowerBound, humanDateFormatShort, i18n)} - ${formatDate(
            upperBound,
            humanDateFormatShort,
            i18n
        )}`;

        const currentMonthFormatted = formatDate(upperBound, humanDateFormatLong, i18n);
        const {filterOfferType, pathOfferType, cityRouteRoot, propertyTypeChart1, propertyTypeRest} = homeConfig;

        const chartOfferTypeProps = filterOfferType ? {offerType: filterOfferType} : {};

        return (
            <Layout showCityAutoselect={false}>
                {biggestRegionsStatsData && biggestRegionsTypeStatsData && chartData && (
                    <Grid type="home">
                        <Card
                            area="chart1"
                            title={
                                <Trans>{t("home.chart1.title", {periodDates, propertyType: propertyTypeChart1})}</Trans>
                            }
                            helper={<Trans>{t("home.chart1.description", {propertyType: propertyTypeChart1})}</Trans>}
                            chartRef={chart1Ref}
                            withTextBlock
                        >
                            <MultilineSharedChart
                                title={
                                    <Trans>
                                        {t("home.chart1.title", {periodDates, propertyType: propertyTypeChart1})}
                                    </Trans>
                                }
                                colorSet="alternate"
                                data={chartData}
                                dataField="avg_price_m2"
                                dataStatus={chartDataStatus}
                                height={400}
                                unit={` ${t("common.pln_m2")}`}
                                innerRef={chart1Ref}
                                roundValues
                                {...chartOfferTypeProps}
                            />

                            <ExtendableTextBlock title={t("home.analyst_comment_heading")} contentHeight={400}>
                                <Error>
                                    <AnalystComment
                                        type="avg_price_m2"
                                        offerType={filterOfferType}
                                        biggestRegionsStatsData={biggestRegionsStatsData}
                                        biggestRegionsTypeStatsData={biggestRegionsTypeStatsData}
                                    />
                                </Error>
                            </ExtendableTextBlock>
                        </Card>

                        <Card
                            area="map"
                            title={
                                <Trans>
                                    {t("home.map.title", {
                                        currentMonth: currentMonthFormatted,
                                        propertyType: propertyTypeRest
                                    })}
                                </Trans>
                            }
                            helper={
                                <Trans>
                                    {t("home.map.description", {
                                        currentMonth: currentMonthFormatted,
                                        propertyType: propertyTypeRest
                                    })}
                                </Trans>
                            }
                            withTextBlock
                        >
                            <div>
                                <CountryMap<IMonthCityStats | IMonthCityTypeStats>
                                    data={mapData}
                                    dataStatus={chartDataStatus}
                                    {...chartOfferTypeProps}
                                />
                            </div>

                            <ExtendableTextBlock title={t("home.analyst_comment_heading")} contentHeight={700}>
                                <Error>
                                    <AnalystComment
                                        type="available_offers"
                                        offerType={filterOfferType}
                                        biggestRegionsStatsData={biggestRegionsStatsData}
                                        biggestRegionsTypeStatsData={biggestRegionsTypeStatsData}
                                    />
                                </Error>
                            </ExtendableTextBlock>
                        </Card>

                        <Card
                            area="chart2"
                            title={
                                <Trans>{t("home.chart2.title", {periodDates, propertyType: propertyTypeRest})}</Trans>
                            }
                            helper={<Trans>{t("home.chart2.description", {propertyType: propertyTypeRest})}</Trans>}
                            chartRef={chart2Ref}
                        >
                            <MultilineSharedChart
                                title={
                                    <Trans>
                                        {t("home.chart2.title", {periodDates, propertyType: propertyTypeRest})}
                                    </Trans>
                                }
                                colorSet="alternate"
                                data={chartData}
                                dataField="available_offers"
                                dataStatus={chartDataStatus}
                                height={300}
                                innerRef={chart2Ref}
                                {...chartOfferTypeProps}
                            />
                        </Card>

                        <Card
                            area="chart3"
                            title={
                                <Trans>{t("home.chart3.title", {periodDates, propertyType: propertyTypeRest})}</Trans>
                            }
                            helper={
                                <>
                                    {t("home.chart3.description", {propertyType: propertyTypeRest})}
                                    <br />
                                    <br />
                                    <MathEquation
                                        leftSide={t("common.formulas.number_of_offers")}
                                        rightSide={`${t("common.formulas.sold_offers")} + ${t(
                                            "common.formulas.available_offers"
                                        )}`}
                                    />
                                    <br />
                                    <MathEquation
                                        leftSide={t("common.formulas.demand_indicator")}
                                        rightSide={
                                            <Division
                                                dividend={t("common.formulas.sold_offers")}
                                                divisor={t("common.formulas.number_of_offers")}
                                            />
                                        }
                                    />
                                </>
                            }
                            chartRef={chart3Ref}
                        >
                            <MultilineSharedChart
                                title={
                                    <Trans>
                                        {t("home.chart3.title", {periodDates, propertyType: propertyTypeRest})}
                                    </Trans>
                                }
                                colorSet="alternate"
                                data={chartData}
                                dataField="demand"
                                dataStatus={chartDataStatus}
                                height={300}
                                unit="%"
                                valueMultiplier={100}
                                innerRef={chart3Ref}
                                {...chartOfferTypeProps}
                            />
                        </Card>

                        <Card
                            area="chart4"
                            title={
                                <Trans>{t("home.chart4.title", {periodDates, propertyType: propertyTypeRest})}</Trans>
                            }
                            helper={<Trans>{t("home.chart4.description", {propertyType: propertyTypeRest})}</Trans>}
                            chartRef={chart4Ref}
                        >
                            <MultilineSharedChart
                                title={
                                    <Trans>
                                        {t("home.chart4.title", {periodDates, propertyType: propertyTypeRest})}
                                    </Trans>
                                }
                                colorSet="alternate"
                                data={chartData}
                                dataField="sold"
                                dataStatus={chartDataStatus}
                                height={300}
                                innerRef={chart4Ref}
                                {...chartOfferTypeProps}
                            />
                        </Card>

                        <Card
                            area="chart5"
                            title={
                                <Trans>{t("home.chart5.title", {periodDates, propertyType: propertyTypeRest})}</Trans>
                            }
                            helper={<Trans>{t("home.chart5.description", {propertyType: propertyTypeRest})}</Trans>}
                            chartRef={chart5Ref}
                        >
                            <MultilineSharedChart
                                title={
                                    <Trans>
                                        {t("home.chart5.title", {periodDates, propertyType: propertyTypeRest})}
                                    </Trans>
                                }
                                colorSet="alternate"
                                data={chartData}
                                dataField="added"
                                dataStatus={chartDataStatus}
                                height={300}
                                innerRef={chart5Ref}
                                {...chartOfferTypeProps}
                            />
                        </Card>
                    </Grid>
                )}
            </Layout>
        );
    })
);
